import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../contexts/SupabaseAuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Progress } from '../../components/ui/Progress';
import { WelcomeStep } from '../../components/onboarding/WelcomeStep';
import { ProjectSetupStep } from '../../components/onboarding/ProjectSetupStep';
import { GA4OAuthStep } from '../../components/onboarding/GA4OAuthStep';
import { GA4PropertySelectionStep } from '../../components/onboarding/GA4PropertySelectionStep';
import { FirstQuestionStep } from '../../components/onboarding/FirstQuestionStep';
import { useProjects } from '../../hooks/useProjects';
import { useAgents } from '../../hooks/useAgents';
import { toast } from 'sonner';
import { ArrowLeft, ArrowRight, Moon, Sun } from 'lucide-react';
import { googleAnalyticsService } from '../../services/googleAnalytics';

const STEPS = [
  { id: 'welcome', title: 'Welcome', component: WelcomeStep, path: '1' },
  { id: 'project', title: 'Project Setup', component: ProjectSetupStep, path: '2' },
  { id: 'ga4-oauth', title: 'Connect GA4', component: GA4OAuthStep, path: '3' },
  { id: 'ga4-property', title: 'Select Property', component: GA4PropertySelectionStep, path: '4' },
  { id: 'question', title: 'First Question', component: FirstQuestionStep, path: '5' },
];

export function OnboardingPage() {
  const { step } = useParams();
  const navigate = useNavigate();
  const [stepData, setStepData] = useState<Record<string, any>>({});
  const { user, userProfile, updateProfile } = useAuth();
  const { setupOnboardingProject, updateProject } = useProjects();
  const { chat } = useAgents();
  const { theme, toggleTheme } = useTheme();

  // Determine current step from URL
  const currentStepIndex = step ? STEPS.findIndex(s => s.path === step) : 0;
  const validStepIndex = currentStepIndex >= 0 ? currentStepIndex : 0;
  
  // Check if onboarding is already completed and redirect to dashboard
  useEffect(() => {
    if (userProfile?.onboardingCompleted) {
      navigate('/dashboard', { replace: true });
      return;
    }
  }, [userProfile, navigate]);

  // Redirect to step 1 if no step in URL
  useEffect(() => {
    if (!step) {
      navigate('/onboarding/1', { replace: true });
    }
  }, [step, navigate]);

  const currentStepConfig = STEPS[validStepIndex];
  const StepComponent = currentStepConfig?.component || WelcomeStep;
  const progress = ((validStepIndex + 1) / STEPS.length) * 100;

  const handleNext = async (data?: any) => {
    try {
      if (data) {
        setStepData(prev => ({ ...prev, [currentStepConfig.id]: data }));
      }

      // Handle project setup step - save to database
      if (currentStepConfig.id === 'project' && data) {
        toast.loading('Setting up your project...');
        
        try {
          const projectResult = await setupOnboardingProject({
            projectName: data.projectName,
            website: data.website,
            industry: data.industry,
            teamSize: data.teamSize,
            description: data.description
          });
          
          // Store the project ID for the next steps
          setStepData(prev => ({ 
            ...prev, 
            [currentStepConfig.id]: { 
              ...data, 
              projectId: projectResult.project?.id 
            } 
          }));
          
          toast.dismiss();
          toast.success('Project setup completed!');
        } catch (error) {
          toast.dismiss();
          toast.error('Failed to setup project. Please try again.');
          console.error('Project setup error:', error);
          return; // Don't advance to next step on error
        }
      }

      // Handle GA4 OAuth step (step 3)
      if (currentStepConfig.id === 'ga4-oauth' && data) {
        if (data.skipped) {
          // User skipped GA4 OAuth connection
          toast.info('GA4 connection skipped. You can connect later from your project settings.');
          console.log('GA4 OAuth connection skipped during onboarding');
        }
        // For OAuth step, we just store the data and continue to property selection
      }

      // Handle GA4 property selection step (step 4)
      if (currentStepConfig.id === 'ga4-property' && data) {
        if (data.skipped) {
          // User skipped GA4 property selection - mark onboarding as complete
          toast.info('GA4 property selection skipped. You can connect later from your project settings.');
          console.log('GA4 property selection skipped during onboarding');
          
          // Mark onboarding as complete since they chose to skip
          await updateProfile({ onboarding_completed: true });
          toast.success('🎉 Welcome to Crutan! You can connect analytics later.');
          setTimeout(() => {
            navigate('/dashboard', { replace: true });
          }, 1500);
          return;
        } else if (data.completed && data.connectionMethod === 'manual' && data.connectionDetails) {
          // User completed manual GA4 property connection - onboarding is now complete
          console.log('Manual GA4 connection completed:', data.connectionDetails);
          
          setStepData(prev => ({ 
            ...prev, 
            [currentStepConfig.id]: { 
              ...data,
              isConnected: true,
              completed: true
            } 
          }));
          
          // Since GA4 is connected, onboarding should be complete
          // The component already marked onboarding_completed: true
          toast.success('🎉 Welcome to Crutan! Your analytics are connected!');
          setTimeout(() => {
            navigate('/dashboard', { replace: true });
          }, 1500);
          return;
        } else if (data.completed && data.selectedProperty && data.selectedAccount) {
          // Legacy handling for property selection without direct connection
          console.log('GA4 property selected (legacy mode):', {
            accountId: data.selectedAccount.accountId,
            propertyId: data.selectedProperty.propertyId,
            propertyName: data.selectedProperty.propertyName
          });
          
          setStepData(prev => ({ 
            ...prev, 
            [currentStepConfig.id]: { 
              ...data,
              isConnected: true,
              completed: true
            } 
          }));
        }
      }

      // Handle first question step
      if (currentStepConfig.id === 'question' && data && !data.skipped) {
        if (data.question && data.response === null) {
          // User just asked a question, make AI call
          toast.loading('Getting AI response...');
          
          try {
            // Use the project ID from the project setup step
            const projectId = stepData.project?.projectId;
            
            // Send question to AI agent
            const response = await chat({
              agentType: 'data',
              message: data.question,
              projectId
            });
            
            // Update step data with response and stay on same step
            setStepData(prev => ({
              ...prev,
              question: {
                question: data.question,
                response: response.response
              }
            }));
            
            toast.dismiss();
            toast.success('AI response received!');
            return; // Don't advance to next step, stay here to show response
          } catch (error) {
            toast.dismiss();
            toast.error('Failed to get AI response. Please try again.');
            console.error('AI response error:', error);
            
            // Update step data to show error state
            setStepData(prev => ({
              ...prev,
              question: {
                question: data.question,
                response: null,
                error: true
              }
            }));
            return; // Don't advance to next step
          }
        } else if (data.completed) {
          // User clicked "Continue to Chat" after seeing the response
          // Mark onboarding as complete and redirect to data agent with pre-loaded question
          await updateProfile({ onboarding_completed: true });
          
          toast.success('🎉 Welcome to Crutan! Redirecting to chat...');
          
          // Redirect to data agent conversation with question as URL parameter
          const encodedQuestion = encodeURIComponent(data.question);
          setTimeout(() => {
            navigate(`/agents/data/conversation?question=${encodedQuestion}`, { replace: true });
          }, 1000);
          return;
        } else {
          // User skipped - mark onboarding as complete and redirect to dashboard
          await updateProfile({ onboarding_completed: true });
          toast.success('🎉 Welcome to Crutan!');
          setTimeout(() => {
            navigate('/dashboard', { replace: true });
          }, 1000);
        }
      }

      // Navigate to next step or complete onboarding
      if (validStepIndex < STEPS.length - 1) {
        const nextStep = STEPS[validStepIndex + 1];
        navigate(`/onboarding/${nextStep.path}`, { replace: true });
      } else {
        // Final step - should be handled by the step-specific logic above
        // If we reach here, it means the step didn't handle completion properly
        console.warn('Reached end of onboarding without proper step handling');
        await completeOnboarding();
      }
    } catch (error) {
      console.error('Onboarding step error:', error);
      toast.error('Something went wrong. Please try again.');
    }
  };

  const handleBack = () => {
    if (validStepIndex > 0) {
      const prevStep = STEPS[validStepIndex - 1];
      navigate(`/onboarding/${prevStep.path}`, { replace: true });
    }
  };

  const completeOnboarding = async () => {
    toast.loading('Completing onboarding...');
    try {
      // Mark onboarding as complete (GA4 connection is optional)
      await updateProfile({ onboarding_completed: true });
      toast.dismiss();
      
      const ga4Data = stepData['ga4-property'];
      if (ga4Data?.completed) {
        toast.success('🎉 Welcome to Crutan! Your GA4 setup is complete!');
      } else {
        toast.success('🎉 Welcome to Crutan! You can connect GA4 later from your project settings.');
      }
      
      // Small delay to ensure auth state is updated, then redirect to dashboard
      setTimeout(() => {
        navigate('/dashboard', { replace: true });
      }, 1500);
    } catch (error) {
      toast.dismiss();
      toast.error('Failed to complete onboarding. Please try again.');
      console.error('Onboarding completion error:', error);
    }
  };

  const handleSkip = () => {
    // Skip current step and go to next, or complete if on last step
    if (validStepIndex < STEPS.length - 1) {
      const nextStep = STEPS[validStepIndex + 1];
      navigate(`/onboarding/${nextStep.path}`, { replace: true });
    } else {
      completeOnboarding();
    }
  };

  if (!currentStepConfig) {
    // Invalid step, redirect to step 1
    navigate('/onboarding/1', { replace: true });
    return null;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900">
      <div className="container mx-auto px-4 py-8">
        {/* Theme Toggle Button */}
        <div className="absolute top-6 right-6">
          <Button
            variant="outline"
            size="icon"
            onClick={toggleTheme}
            className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm border-gray-200 dark:border-gray-700 hover:bg-white dark:hover:bg-gray-800 text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white"
          >
            {theme === 'dark' ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
          </Button>
        </div>

        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-r from-blue-600 to-purple-600 rounded-xl mb-4">
            <span className="text-white font-bold text-xl">C</span>
          </div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Welcome to Crutan</h1>
          <p className="text-gray-600 dark:text-gray-300 mt-2">Let's get you set up in just a few steps</p>
        </div>

        {/* Progress */}
        <div className="max-w-2xl mx-auto mb-8">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
              Step {validStepIndex + 1} of {STEPS.length}
            </span>
            <span className="text-sm text-gray-500 dark:text-gray-400">{currentStepConfig.title}</span>
          </div>
          <Progress value={progress} className="h-2" />
        </div>

        {/* Step Content */}
        <div className="max-w-2xl mx-auto">
          <Card className="p-8">
            <StepComponent
              data={{
                ...stepData[currentStepConfig.id],
                // Always pass project ID to GA4 steps if available
                ...(stepData.project?.projectId ? {
                  projectId: stepData.project.projectId
                } : {})
              }}
              onNext={handleNext}
              onBack={handleBack}
              onSkip={handleSkip}
              isFirst={validStepIndex === 0}
              isLast={validStepIndex === STEPS.length - 1}
            />
          </Card>

          {/* Navigation */}
          {validStepIndex > 0 && validStepIndex < STEPS.length - 1 && (
            <div className="flex items-center justify-between mt-6">
              <Button variant="ghost" onClick={handleBack}>
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back
              </Button>
              <Button variant="ghost" onClick={handleSkip}>
                Skip for now
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}